"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, ShieldCheck, Zap, MapPin, ArrowRight, PhoneCall, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const highlightCards = [{
  icon: Zap,
  title: "Rapid Local Dispatch",
  description: "Plumbers stationed right in Jonesboro for fast arrivals when tank leaks or heating failures strike your home."
}, {
  icon: ShieldCheck,
  title: "Clayton County Code Compliant",
  description: "Every water heater installation and repair meets current Clayton County and Georgia safety plumbing codes."
}, {
  icon: Clock,
  title: "24/7 Emergency Availability",
  description: "Day or night, weekend or holiday, our emergency team is ready to restore your hot water without delay."
}];
const coverageTowns = ["Jonesboro Historic District", "Tara Boulevard Corridor", "Lake Spivey Area", "Morrow & Southlake", "Riverdale Border", "Lovejoy & Irondale"];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="jonesboroservicearea" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="flex flex-col gap-12">
          {/* Header & Eyebrow */}
          <div className="max-w-3xl">
            <motion.div variants={itemVariants}>
              <Badge variant="outline" className="mb-4 gap-1.5 border-primary/20 bg-secondary text-secondary-foreground font-semibold px-3 py-1">
                <MapPin className="size-3.5 text-accent" />
                <span>Clayton County Coverage</span>
              </Badge>
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
              Water Heater Service in Jonesboro, GA
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              When your water heater stops heating or starts leaking, you need an experienced local plumber on-site fast. We deliver prompt water heater repairs, energy-efficient replacements, and urgent emergency response across all of Jonesboro and neighboring Clayton County communities.
            </motion.p>
          </div>

          {/* Service Feature Highlights */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlightCards.map((card, i) => {
            const IconComponent = card.icon;
            return <motion.div key={card.title} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200">
                    <CardHeader className="space-y-4">
                      <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        <IconComponent className="size-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {card.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {card.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Local Area Coverage Details Box */}
          <motion.div variants={itemVariants} className="rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 lg:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
                  Serving Every Corner of Jonesboro & Beyond
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Our fully stocked service vans travel daily along Tara Blvd, GA-138, and Highway 54, allowing us to arrive prepared with all common replacement valves, heating elements, thermostats, and replacement tanks.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                  {coverageTowns.map((town, idx) => <div key={town} data-index={idx} className="flex items-center gap-2 text-sm text-foreground font-medium">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>{town}</span>
                    </div>)}
                </div>
              </div>

              {/* Call-to-Actions */}
              <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm justify-between">
                  <Link to="/service-areas/">
                    <span>View All Service Locations</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full border-border bg-background text-foreground hover:bg-muted font-medium justify-between">
                  <Link to="/services/">
                    <span>Explore Plumbing Services</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="secondary" size="lg" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 font-medium justify-between">
                  <Link to="/contact/">
                    <span>Request Service Dispatch</span>
                    <PhoneCall className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}